import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { resolveMediaUrl } from '../lib/mediaUrl';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const DEFAULT_BRAND = 'Snagcart';

const WhitelabelBrandContext = createContext({
  brandName: DEFAULT_BRAND,
  logoUrl: null,
  logoMissing: false,
  loading: false,
  refresh: async () => {},
  applyBrand: () => {},
});

async function fetchBrandForUser(user) {
  if (!user) {
    return { brandName: DEFAULT_BRAND, logoUrl: null, logoMissing: false };
  }

  // Agency client — brand comes from their agency landing config
  if (user.whitelabel_slug) {
    const res = await fetch(`${API_URL}/api/whitelabel/landing/${user.whitelabel_slug}`);
    if (!res.ok) return { brandName: DEFAULT_BRAND, logoUrl: null, logoMissing: false };
    const cfg = await res.json();
    return {
      brandName: (cfg.brand_name || DEFAULT_BRAND).trim() || DEFAULT_BRAND,
      logoUrl: resolveMediaUrl(cfg.logo_url) || null,
      logoMissing: Boolean(cfg.logo_missing),
    };
  }

  const token = localStorage.getItem('token');
  if (!token) return { brandName: DEFAULT_BRAND, logoUrl: null, logoMissing: false };

  // Any logged-in owner with white-label access (Growth / platform admin).
  // Always try /me — 403 means no agency branding configured for this plan.
  const res = await fetch(`${API_URL}/api/whitelabel/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    return { brandName: DEFAULT_BRAND, logoUrl: null, logoMissing: false };
  }
  const data = await res.json();
  const name = (data.brand_name || '').trim();
  // Scaffold defaults use email prefix — still show it when logo/brand were saved
  return {
    brandName: name || DEFAULT_BRAND,
    logoUrl: resolveMediaUrl(data.logo_url) || null,
    logoMissing: Boolean(data.logo_missing),
  };
}

export function WhitelabelBrandProvider({ user, children }) {
  const [brandName, setBrandName] = useState(DEFAULT_BRAND);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoMissing, setLogoMissing] = useState(false);
  const [loading, setLoading] = useState(false);

  const applyBrand = useCallback((next) => {
    if (!next || typeof next !== 'object') return;
    if (next.brand_name != null || next.brandName != null) {
      const name = String(next.brand_name ?? next.brandName ?? '').trim();
      setBrandName(name || DEFAULT_BRAND);
    }
    if ('logo_url' in next || 'logoUrl' in next) {
      const raw = next.logo_url ?? next.logoUrl;
      setLogoUrl(raw ? resolveMediaUrl(raw) : null);
      setLogoMissing(false);
    }
    if ('logo_missing' in next) {
      setLogoMissing(Boolean(next.logo_missing));
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchBrandForUser(user);
      setBrandName(next.brandName);
      setLogoUrl(next.logoUrl);
      setLogoMissing(next.logoMissing);
    } catch {
      setBrandName(DEFAULT_BRAND);
      setLogoUrl(null);
      setLogoMissing(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const next = await fetchBrandForUser(user);
        if (!cancelled) {
          setBrandName(next.brandName);
          setLogoUrl(next.logoUrl);
          setLogoMissing(next.logoMissing);
        }
      } catch {
        if (!cancelled) {
          setBrandName(DEFAULT_BRAND);
          setLogoUrl(null);
          setLogoMissing(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.snagcart_plan, user?.is_platform_admin, user?.whitelabel_slug]);

  const value = useMemo(
    () => ({ brandName, logoUrl, logoMissing, loading, refresh, applyBrand }),
    [brandName, logoUrl, logoMissing, loading, refresh, applyBrand],
  );

  return (
    <WhitelabelBrandContext.Provider value={value}>
      {children}
    </WhitelabelBrandContext.Provider>
  );
}

export function useWhitelabelBrand() {
  return useContext(WhitelabelBrandContext);
}

export default WhitelabelBrandContext;
